/**
 * Active Ops heat map style presets — same options as Favorites 2D map style menu.
 */
import { NIGHT_MODE_STYLE } from '../constants/mapStyles';

export type ActiveOpsMapStyleId =
  | 'hybrid'
  | 'satellite'
  | 'terrain'
  | 'cyber'
  | 'night'
  | 'roads'
  | 'desert'
  | 'topo';

export const ACTIVE_OPS_MAP_STYLE_OPTIONS: Array<{
  value: ActiveOpsMapStyleId;
  label: string;
}> = [
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'satellite', label: 'Satellite' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'cyber', label: 'Cyber' },
  { value: 'night', label: 'Night' },
  { value: 'roads', label: 'Roads' },
  { value: 'desert', label: 'Desert' },
  { value: 'topo', label: 'Topo' },
];

export const ACTIVE_OPS_MAP_STYLE_STORAGE_KEY = 'active-ops-map-style';

const ROAD_LABEL_EMPHASIS: google.maps.MapTypeStyle[] = [
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'on' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#0b1120' }, { weight: 24 }] },
  { featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ color: '#f8fafc' }, { weight: 24 }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#0f172a' }, { weight: 20 }] },
  { featureType: 'road.arterial', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 20 }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#0f172a' }, { weight: 18 }] },
  { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 18 }] },
];

const withRoadLabels = (styles: google.maps.MapTypeStyle[] = []) => [
  ...styles,
  ...ROAD_LABEL_EMPHASIS,
];

const LIGHT_CYBER: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f0f4ff' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#fef3c7' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bfdbfe' }] },
];

const DARK_CYBER: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#78350f' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#fbbf24' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0c4a6e' }] },
];

const DESERT: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#e8dcc4' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5c4a32' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f5f5dc' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ff8c00' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#4682b4' }] },
];

const ROADS: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d3a5e' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#00d9ff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0a192f' }] },
];

const TOPO: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#e8e4d9' }] },
  { featureType: 'landscape.natural.terrain', elementType: 'geometry', stylers: [{ color: '#d4c4a8' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ff6b35' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#7eb5d6' }] },
];

export function isActiveOpsMapStyleId(v: string | null | undefined): v is ActiveOpsMapStyleId {
  return ACTIVE_OPS_MAP_STYLE_OPTIONS.some((o) => o.value === v);
}

export function readStoredActiveOpsMapStyle(
  fallback: ActiveOpsMapStyleId = 'hybrid',
): ActiveOpsMapStyleId {
  try {
    if (typeof localStorage === 'undefined') return fallback;
    const raw = localStorage.getItem(ACTIVE_OPS_MAP_STYLE_STORAGE_KEY);
    return isActiveOpsMapStyleId(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

export function storeActiveOpsMapStyle(style: ActiveOpsMapStyleId): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(ACTIVE_OPS_MAP_STYLE_STORAGE_KEY, style);
  } catch {
    // ignore quota / private mode
  }
}

export function applyActiveOpsMapStyle(
  map: google.maps.Map,
  style: ActiveOpsMapStyleId,
  darkMode: boolean,
): void {
  switch (style) {
    case 'night':
      map.setMapTypeId('roadmap');
      map.setOptions({ styles: withRoadLabels(NIGHT_MODE_STYLE) });
      break;
    case 'cyber':
      map.setMapTypeId('roadmap');
      map.setOptions({
        styles: withRoadLabels(darkMode ? DARK_CYBER : LIGHT_CYBER),
      });
      break;
    case 'terrain':
      map.setMapTypeId('terrain');
      map.setOptions({ styles: withRoadLabels() });
      break;
    case 'satellite':
      map.setMapTypeId('satellite');
      map.setOptions({ styles: withRoadLabels() });
      break;
    case 'hybrid':
      map.setMapTypeId('hybrid');
      map.setOptions({ styles: withRoadLabels() });
      break;
    case 'desert':
      map.setMapTypeId('roadmap');
      map.setOptions({ styles: withRoadLabels(DESERT) });
      break;
    case 'roads':
      map.setMapTypeId('roadmap');
      map.setOptions({ styles: withRoadLabels(ROADS) });
      break;
    case 'topo':
      map.setMapTypeId('terrain');
      map.setOptions({ styles: withRoadLabels(TOPO) });
      break;
    default:
      map.setMapTypeId('hybrid');
      map.setOptions({ styles: withRoadLabels() });
  }
}
